import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { assistantAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Sparkles, ArrowRight, AlertTriangle, CheckCircle2, Info, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const Assistant = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await assistantAPI.getInsights();
      setInsights(res.data.insights || []);
    } catch (error) {
      toast.error('No se pudieron cargar los análisis financieros.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  const getIconForType = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'success': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'info':
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getVariantForType = (type) => {
    switch (type) {
      case 'warning': return 'bg-amber-50 border-amber-100';
      case 'success': return 'bg-emerald-50 border-emerald-100';
      case 'info':
      default: return 'bg-blue-50 border-blue-100';
    }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col pt-4" data-testid="assistant-page">
      {/* Header */}
      <div className="mb-6 px-1 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-medium text-2xl lg:text-3xl text-slate-900 tracking-tight flex items-center gap-2">
            Panel de Decisiones
            <Sparkles className="w-5 h-5 text-emerald-600" />
          </h1>
          <p className="text-slate-500 text-[15px] mt-1.5 leading-relaxed max-w-2xl">
            Análisis silencioso de tu actividad financiera reciente.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchInsights}
          disabled={loading}
          className="rounded-sm bg-white border-slate-200 text-slate-600 shadow-sm"
        >
          {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Actualizar Análisis
        </Button>
      </div>

      {/* Main Content */}
      <ScrollArea className="flex-1 -mx-4 px-4 md:mx-0 md:px-0">
        <div className="space-y-4 max-w-4xl pb-10">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-sm border border-slate-100 shadow-sm">
              <Sparkles className="w-8 h-8 text-emerald-900 animate-pulse mb-4" />
              <p className="text-slate-600 font-medium">Core Cognitivo procesando datos...</p>
              <p className="text-sm text-slate-400 mt-1">Calculando variaciones mensuales e identificando patrones.</p>
            </div>
          ) : insights.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-sm border border-slate-100 shadow-sm">
              <Info className="w-8 h-8 text-slate-300 mb-4" />
              <p className="text-slate-600 font-medium">Aún no hay suficientes datos para extraer conclusiones seguras.</p>
              <p className="text-sm text-slate-400 mt-1">Registra más gastos en los próximos días.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {insights.map((insight, idx) => (
                <Card key={idx} className={cn("rounded-sm border shadow-sm transition-all hover:shadow-md", getVariantForType(insight.type))}>
                  <CardContent className="p-5 flex flex-col sm:flex-row items-start gap-4">
                    <div className="flex-shrink-0 mt-0.5 bg-white p-2 rounded-full shadow-sm">
                      {getIconForType(insight.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 text-base">{insight.title}</h3>
                      <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                        {insight.description}
                      </p>

                      {insight.action_text && insight.action_link && (
                        <div className="mt-4">
                          <Button
                            asChild
                            variant="default"
                            className="rounded-sm bg-white text-slate-800 border-slate-200 shadow-sm hover:bg-slate-50"
                            size="sm"
                          >
                            <Link to={insight.action_link}>
                              {insight.action_text}
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Legal AI Note */}
          <div className="mt-8 text-center flex flex-col lg:flex-row items-center justify-center gap-2">
            <p className="text-[11px] text-slate-400">
              <span className="font-semibold text-slate-500">Inteligencia Analítica:</span> Los insights son cálculos predictivos y no constituyen asesoramiento financiero profesional vinculante.
            </p>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default Assistant;
